'use client';

import type { VariantProps } from 'class-variance-authority';
import { endOfMonth, isBefore, parse } from 'date-fns';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CardIcons } from '@/app/(main)/client-detail/consts/client-detail';
import { useCardType } from '@/app/(main)/client-detail/hooks/useCardType';
import type { CardType } from '@/app/(main)/client-detail/types';
import { USER_ROLES } from '@/consts/auth';
import { useUser } from '@/context/UserProvider';
import { formatSabreFOP } from '@/utils/formatSabreFOP';
import { Tooltip } from '../../Tooltip';
import { TooltipWrapper } from '../../Tooltip/components/TooltipWrapper';
import { cardStyles } from './styles';
import { renderDots } from './utils/Dots';
import CopyIcon from '/public/icons/copy.svg';
import DangerAlertIcon from '/public/icons/danger-triangle.svg';
import EyeCrossedIcon from '/public/icons/eye-crossed.svg';
import EyeIcon from '/public/icons/eye.svg';
import SabreCopyIcon from '/public/icons/sabre-copy.svg';

interface CreditCardProps extends Omit<VariantProps<typeof cardStyles>, 'variant'> {
  variant?: 'default' | 'addNew' | 'expired';
  cardholderName?: string;
  cardNumber?: string;
  expiryDate?: string;
  cardType?: string;
  nickname?: string | null;
  last4?: string;
  cvv?: string;
  initialShowDetails?: boolean;
}

export const CreditCard: React.FC<CreditCardProps> = ({
  variant = 'default',
  cardholderName,
  cardNumber,
  expiryDate,
  nickname,
  cardType,
  last4,
  cvv,
  initialShowDetails = false,
}) => {
  const [showDetails, setShowDetails] = useState(initialShowDetails);
  const shouldShowSensitiveDetails = showDetails;
  const { user } = useUser();
  const canShowSabreCopy = user?.role === USER_ROLES.AIR_TEAM || user?.role === USER_ROLES.SUPER_ADMIN;
  const { Icon } = useCardType(cardNumber ?? '');
  const last4Digits = last4 || cardNumber?.slice(-4);
  const hiddenCvvDotCount = (cvv?.replace(/\D/g, '').length ?? 0) === 4 ? 4 : 3;
  const PlaceholderIcon = cardType ? CardIcons[cardType as keyof typeof CardIcons] : null;

  const toggleDetails = () => setShowDetails(!showDetails);

  const copyCreditCardSabreViewFormat = () => {
    if (!expiryDate || !cardType) return;
    const numberToUse = cardNumber || last4Digits || '';
    const sabreFOPFormat = formatSabreFOP(cardType as CardType, numberToUse, expiryDate);
    navigator.clipboard.writeText(sabreFOPFormat);
    toast.success('Card copied in Sabre format!');
  };

  const copyCreditCardNumbersOnly = () => {
    if (cardNumber) {
      navigator.clipboard.writeText(cardNumber.replace(/\s/g, ''));
      toast.success('Card number successfully copied!');
    }
  };

  const copyLast4 = () => {
    if (last4Digits) {
      navigator.clipboard.writeText(last4Digits);
      toast.success('Card number copied!');
    }
  };

  const isActuallyExpired = expiryDate
    ? isBefore(endOfMonth(parse(expiryDate, 'MM/yy', new Date())), new Date())
    : false;

  let logicalState: 'default' | 'addNew' | 'noData' | 'expired' | 'error';
  let styleVariant: 'default' | 'addNew' | 'expired' | 'error';

  const hasRevealedDetails = Boolean(cardNumber);

  switch (true) {
    case isActuallyExpired && hasRevealedDetails:
      logicalState = 'expired';
      styleVariant = 'expired';
      break;
    case isActuallyExpired:
      logicalState = 'error';
      styleVariant = 'error';
      break;
    case variant === 'expired':
      logicalState = 'expired';
      styleVariant = 'expired';
      break;
    case variant === 'addNew':
      logicalState = 'addNew';
      styleVariant = 'addNew';
      break;
    case hasRevealedDetails:
      logicalState = 'default';
      styleVariant = 'default';
      break;
    case !cardholderName:
      logicalState = 'noData';
      styleVariant = 'default';
      break;
    default:
      logicalState = 'default';
      styleVariant = 'default';
      break;
  }

  const showPlaceholders = logicalState === 'addNew' || logicalState === 'error';
  const showCardNumberRow =
    logicalState === 'default' || logicalState === 'noData' || logicalState === 'expired' || variant === 'expired';
  const showCardholderActions =
    logicalState === 'default' || (logicalState === 'expired' && hasRevealedDetails);
  const displayCardholderName = cardholderName || nickname || '';

  const renderCardholderActions = () => (
    <div className="flex shrink-0 gap-[10px]">
      {canShowSabreCopy && (
        <Tooltip content={<TooltipWrapper>Copy Sabre Format</TooltipWrapper>}>
          <button
            className="cursor-pointer"
            onClick={copyCreditCardSabreViewFormat}
            disabled={!cardNumber || !expiryDate || !cardType || !shouldShowSensitiveDetails}
          >
            <SabreCopyIcon
              className="stroke-current text-grey-700 hover:stroke-blue-300 w-[23px] h-[23px]"
              width={23}
              height={23}
            />
          </button>
        </Tooltip>
      )}
      <span>
        <button
          type="button"
          onClick={toggleDetails}
          className="cursor-pointer"
          aria-label={shouldShowSensitiveDetails ? 'Hide card details' : 'Show card details'}
        >
          {shouldShowSensitiveDetails ? (
            <EyeIcon className="w-5 h-5 text-blue-300 fill-current mt-[2px]" />
          ) : (
            <EyeCrossedIcon className="w-5 h-5 mt-[2px]" />
          )}
        </button>
      </span>
    </div>
  );

  return (
    <div className={cardStyles({ variant: styleVariant, showDetails })}>
      {logicalState === 'expired' && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <DangerAlertIcon />
            <p className="text-body-regular text-black">This card is expired</p>
          </div>
          {hasRevealedDetails && (
            <div className="flex min-w-0 items-center justify-between gap-2">
              <p className="min-w-0 truncate text-body-regular text-black">{displayCardholderName}</p>
              {renderCardholderActions()}
            </div>
          )}
        </div>
      )}

      {logicalState === 'error' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DangerAlertIcon />
            <p className="text-body-regular text-black">This card is expired</p>
          </div>
        </div>
      )}

      {logicalState === 'noData' && (
        <Tooltip
          placement="top"
          isVisible={(nickname?.length ?? 0) > 35}
          content={<TooltipWrapper>{nickname}</TooltipWrapper>}
        >
          <p className="text-body-regular text-black min-h-[24px] truncate">{nickname || ''}</p>
        </Tooltip>
      )}

      {logicalState === 'addNew' && (
        <p className="text-body-regular text-black">{`${cardholderName || 'Cardholder name'}`}</p>
      )}

      {showCardholderActions && logicalState === 'default' && (
        <div className="flex min-w-0 items-center justify-between gap-2">
          <p className="min-w-0 truncate text-body-regular text-black">{displayCardholderName}</p>
          {renderCardholderActions()}
        </div>
      )}

      <div className="flex flex-col gap-[22px] mt-auto">
        {showCardNumberRow && (
          <div className="text-body-regular flex items-center">
            <div className="flex items-center gap-x-[16px]">
              {shouldShowSensitiveDetails ? (
                <span className="tracking-[4px]">{cardNumber}</span>
              ) : (
                <>
                  {renderDots({ count: 12, groupSpacing: 16 })} <span className="tracking-[4px]">{last4Digits}</span>
                </>
              )}
            </div>

            {showCardholderActions && (
              <button
                type="button"
                className="cursor-pointer ml-auto"
                onClick={copyCreditCardNumbersOnly}
                disabled={!cardNumber || !shouldShowSensitiveDetails}
                aria-label="Copy card number"
              >
                <CopyIcon
                  className="stroke-current text-grey-700 hover:stroke-blue-300 w-[32px] h-[32px]"
                  width={32}
                  height={32}
                />
              </button>
            )}

            {logicalState === 'noData' && (
              <button
                type="button"
                className="cursor-pointer ml-auto"
                onClick={copyLast4}
                disabled={!last4Digits}
                aria-label="Copy last 4 digits"
              >
                <CopyIcon
                  className="stroke-current text-grey-700 hover:stroke-blue-300 w-[32px] h-[32px]"
                  width={32}
                  height={32}
                />
              </button>
            )}
          </div>
        )}

        {(logicalState === 'addNew' || logicalState === 'error') && (
          <div className="text-body-regular text-black tracking-[4px]">{cardNumber || 'XXXX XXXX XXXX XXXX'}</div>
        )}

        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-y-[6px]">
            <p className="text-button-regular text-grey-700">Expiration date</p>
            <div className="text-body-regular text-black tracking-[4px]">
              {shouldShowSensitiveDetails ? (
                <span>{expiryDate}</span>
              ) : showPlaceholders ? (
                <span>{expiryDate || 'MM/YY'}</span>
              ) : (
                <div className="flex items-center gap-x-[6px]">
                  {renderDots({ count: 2, dotSize: 8 })} <span>/</span> {renderDots({ count: 2, dotSize: 8 })}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-y-[6px]">
            <p className="text-button-regular text-grey-700">CVV</p>
            <div className="text-body-regular text-black tracking-[4px]">
              {shouldShowSensitiveDetails ? (
                <span>{cvv}</span>
              ) : showPlaceholders ? (
                <span>{cvv || 'XXX'}</span>
              ) : (
                <div className="flex items-center h-[24px]">{renderDots({ count: hiddenCvvDotCount, dotSize: 8 })}</div>
              )}
            </div>
          </div>
          {logicalState === 'noData' && PlaceholderIcon ? (
            <PlaceholderIcon width={61} height={28} className="self-end w-[61px] h-[21px]" />
          ) : (
            Icon && <Icon width={61} height={28} className="self-end w-[61px] h-[21px]" />
          )}
        </div>
      </div>
    </div>
  );
};
